import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, Subject, Observable } from 'rxjs';
import { retry, takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersService } from 'app/services/users-service/users.service';
import { Profile } from 'app/models';
import { SnackBarService } from 'app/services/snackbar-service/snackbar.service';
import { SnackbarType } from 'app/constants/Enums';
import { ApiUrl } from 'app/constants/ApiUrls';

@Injectable({
  providedIn: 'root'
})
export class DeactivatedUsersService implements OnDestroy {
  readonly userUpdateEndpoint = ApiUrl.usersV2;

  private _users: ReplaySubject<Profile[]> = new ReplaySubject<Profile[]>();
  public users$: Observable<any> = this._users.asObservable();

  private _error: ReplaySubject<string> = new ReplaySubject<string>();
  public error$: Observable<string> = this._error.asObservable();
  private onDestroy$ = new Subject<void>();

  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService,
    private _usersService: UsersService,
    public snackBar: MatSnackBar,
    private _snackbarService: SnackBarService
  ) {}

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  permanentDelete(userIds: string[], companyId: string): void {

    userIds.forEach((userId, i) => {
      this._http
        .delete(`${ApiUrl.usersV3}/${userId}`)
        .pipe(retry(2))
        .subscribe(
          () => {
            if (i === userIds.length - 1) {
              let success: string;
              let successMsg: string;
              this._usersService.getUsersByCompanyId(companyId, false);
              this._translateService
                .get('USERS_SERVICE.delete_success')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(value => {
                  successMsg = value;
                });
                this._translateService
                  .get('USERS_SERVICE.success')
                  .pipe(takeUntil(this.onDestroy$))
                  .subscribe((value) => (success = value));


                this._snackbarService.showSnackbar(
                  SnackbarType.success,
                  success,
                  successMsg
                );
            }
          },
          error => this._handleUserActivationError(error)
        );
    });
  }

  deactivateUsers(userIds: string[], companyId: string): void {
    userIds.forEach((userId, i) => {
      this._http
        .patch(`${this.userUpdateEndpoint}/${userId}`, { isBlocked: true })
        .pipe(retry(2))
        .subscribe(
          () => {
            if (i === userIds.length - 1) {
              let successMsg: string;
              this._translateService
                .get('USERS_SERVICE.success')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(value => {
                  successMsg = value;
                  let userDeeactive: string;
                  let usersDeeactive : string;

                  this._translateService
                    .get('SNACKBAR.user_deactive_success')
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(value => {
                      userDeeactive = value;
                    });
                  this._translateService
                    .get('SNACKBAR.users_deactive_success')
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(value => {
                      usersDeeactive = value;
                    });

                  const snackbarMessage =
                    userIds.length > 1
                      ? usersDeeactive
                      : userDeeactive;

                  this._snackbarService.showSnackbar(
                    SnackbarType.success,
                    successMsg,
                    snackbarMessage
                  );
                });
              this._usersService.getUsersByCompanyId(companyId, true); // refresh active users
            }
          },
          error => this._handleUserActivationError(error)
        );
    });
  }

  private _handleUserActivationError(error: HttpErrorResponse): void {
    let errorMsg: string;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (errorMsg = value));
    let message = '';
    if (error.error instanceof ErrorEvent) {
      message = `[users-service] ERROR: ${error.error.message}`;
      console.error(message);
    } else {
      message = `[users-service] STATUS: ${error.status}, ERROR: ${error.message}`;
      console.error(message);
    }
    this._snackbarService.showSnackbar(SnackbarType.error, 'Error', message);
  }

  reactivateUser(userIds: string[], companyId): void {
    userIds.forEach((userId, i) => {
      this._http
        .patch(`${this.userUpdateEndpoint}/${userId}`, { isBlocked: false })
        .pipe(retry(2))
        .subscribe(
          () => {
            if (i === userIds.length - 1) {
              let successMsg: string;
              this._translateService
                .get('USERS_SERVICE.success')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(value => {
                  successMsg = value;

                  let userReactive: string;
                  let usersReactive : string;

                  this._translateService
                    .get('SNACKBAR.user_reactive_success')
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(value => {
                      userReactive = value;
                    });
                  this._translateService
                    .get('SNACKBAR.users_reactive_success')
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(value => {
                      usersReactive = value;
                    });

                  const snackbarMessage =
                    userIds.length > 1
                      ? usersReactive
                      : userReactive;

                  this._snackbarService.showSnackbar(
                    SnackbarType.success,
                    successMsg,
                    snackbarMessage
                  );
                });
              this._usersService.getUsersByCompanyId(companyId, false); // refresh active users
            }
          },
          error => this._handleUserActivationError(error)
        );
    });
  }
}
